import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import reportesService, {
  type Filtros,
  type NombreReporte,
} from '../../services/reportesService';
import { filtrosAQuery } from '../../services/reportesService';

/**
 * Marco común de las pantallas de reporte (PROMPT_N°054).
 *
 * Los cinco reportes tienen la misma estructura —título, filtros, totales,
 * tabla y los botones Excel/Imprimir— y repetirla cinco veces garantizaba
 * que terminaran distintos entre sí. Acá queda el armazón; cada reporte
 * pone su contenido.
 *
 * El botón Imprimir abre la ruta `/reportes/<nombre>/imprimir` **con los
 * mismos filtros en la query string**: la vista de impresión vuelve a
 * pedir los datos con esos filtros, así que lo que se imprime es lo que se
 * está viendo, no una consulta distinta.
 */

interface Props {
  nombre: NombreReporte;
  titulo: string;
  descripcion: string;
  filtros: Filtros;
  /** Barra de filtros del reporte. */
  controles: ReactNode;
  /** Bloque "Lectura" (N°056). Va arriba de los totales, no al final. */
  lectura?: ReactNode;
  /** Tarjetas de totales. */
  totales?: ReactNode;
  /** Aviso ámbar sobre el contenido (sin costo, diferencias, etc.). */
  aviso?: ReactNode;
  cargando?: boolean;
  error?: unknown;
  vacio?: boolean;
  mensajeVacio?: string;
  children: ReactNode;
}

export default function MarcoReporte({
  nombre,
  titulo,
  descripcion,
  filtros,
  controles,
  lectura,
  totales,
  aviso,
  cargando,
  error,
  vacio,
  mensajeVacio = 'No hay datos para los filtros aplicados.',
  children,
}: Props) {
  const [bajando, setBajando] = useState(false);
  const [errorExport, setErrorExport] = useState<string | null>(null);

  const exportar = async () => {
    setBajando(true);
    setErrorExport(null);
    try {
      await reportesService.descargarExcel(nombre, filtros);
    } catch (e: any) {
      // El 413 del backend trae su propio mensaje, pero viaja como blob
      // porque la respuesta se pidió así: hay que leerlo para mostrarlo.
      let detalle = 'No se pudo generar el Excel.';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = JSON.parse(await data.text());
          if (txt?.error) detalle = txt.error;
        } catch {
          /* se queda el mensaje genérico */
        }
      } else if (data?.error) {
        detalle = data.error;
      }
      setErrorExport(detalle);
    } finally {
      setBajando(false);
    }
  };

  const urlImprimir = `/reportes/${nombre}/imprimir?${filtrosAQuery(filtros).toString()}`;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Reportes
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {titulo}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {descripcion}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportar}
            disabled={bajando || cargando}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {bajando ? 'Generando…' : 'Excel'}
          </button>
          <Link
            to={urlImprimir}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Printer className="h-4 w-4" />
            Imprimir
          </Link>
        </div>
      </div>

      {errorExport && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-800 dark:text-red-200">
          {errorExport}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        {controles}
      </div>

      {lectura}
      {aviso}
      {totales}

      {/* El ancla permite que una frase clickeable baje hasta la tabla
          después de aplicar su filtro. */}
      <div
        id="tabla-reporte"
        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        {cargando ? (
          <div className="p-10 text-center text-gray-500 dark:text-gray-400">
            Cargando…
          </div>
        ) : error ? (
          <div className="p-10 text-center text-red-600 dark:text-red-400">
            No se pudo cargar el reporte.
          </div>
        ) : vacio ? (
          <div className="p-10 text-center text-gray-500 dark:text-gray-400">
            {mensajeVacio}
          </div>
        ) : (
          <div className="overflow-x-auto">{children}</div>
        )}
      </div>
    </div>
  );
}

/** Tarjeta de total, para que las cinco pantallas se vean igual. */
export function TarjetaTotal({
  etiqueta,
  valor,
  detalle,
  tono = 'neutro',
}: {
  etiqueta: string;
  valor: string;
  detalle?: string;
  tono?: 'neutro' | 'bueno' | 'alerta' | 'malo';
}) {
  const borde = {
    neutro: 'border-gray-200 dark:border-gray-700',
    bueno: 'border-emerald-200 dark:border-emerald-800',
    alerta: 'border-amber-200 dark:border-amber-800',
    malo: 'border-red-300 dark:border-red-800',
  }[tono];
  const color = {
    neutro: 'text-gray-900 dark:text-gray-100',
    bueno: 'text-emerald-700 dark:text-emerald-300',
    alerta: 'text-amber-700 dark:text-amber-300',
    malo: 'text-red-700 dark:text-red-300',
  }[tono];

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border ${borde} p-4`}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">{etiqueta}</p>
      <p className={`text-2xl font-bold ${color}`}>{valor}</p>
      {detalle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{detalle}</p>
      )}
    </div>
  );
}

/** Clases de celda reutilizadas por las tablas de los reportes. */
export const TH =
  'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 whitespace-nowrap';
export const TD = 'px-3 py-2 text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap';
export const TD_NUM = `${TD} text-right tabular-nums`;
